import { useQueries } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Calendar } from "lucide-react";
import { useMemo, useState } from "react";
import { MilestoneDetailDialog } from "@/components/projects/MilestoneDetailDialog";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Milestone } from "@/hooks/use-milestones";
import type { Project } from "@/hooks/use-projects";
import { useProjects } from "@/hooks/use-projects";
import { apiFetch } from "@/lib/api";
import { milestoneKeys } from "@/lib/query";

type ZoomLevel = "month" | "quarter" | "year";

const ALL_PMS = "__all__";

function statusColor(status: string): string {
  switch (status) {
    case "active":
      return "bg-blue-500";
    case "on_hold":
      return "bg-amber-500";
    case "completed":
      return "bg-emerald-500";
    case "draft":
      return "bg-gray-400";
    default:
      return "bg-gray-300";
  }
}

function statusVariant(status: string): "success" | "warning" | "secondary" | "default" {
  switch (status) {
    case "active":
      return "default";
    case "on_hold":
      return "warning";
    case "completed":
      return "success";
    default:
      return "secondary";
  }
}

function getTimelineRange(projects: Project[], zoom: ZoomLevel): { start: Date; end: Date } {
  const now = new Date();
  let earliest = now;
  let latest = now;

  for (const p of projects) {
    if (p.startDate) {
      const d = new Date(p.startDate);
      if (d < earliest) earliest = d;
    }
    if (p.endDate) {
      const d = new Date(p.endDate);
      if (d > latest) latest = d;
    }
  }

  // Pad the range
  const start = new Date(earliest);
  const end = new Date(latest);
  start.setMonth(start.getMonth() - 1);
  end.setMonth(end.getMonth() + 1);

  // Snap to zoom boundaries
  if (zoom === "quarter") {
    start.setMonth(Math.floor(start.getMonth() / 3) * 3, 1);
    end.setMonth(Math.ceil((end.getMonth() + 1) / 3) * 3, 0);
  } else if (zoom === "year") {
    start.setMonth(0, 1);
    end.setMonth(11, 31);
  } else {
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 0);
  }

  return { start, end };
}

function generateColumns(
  start: Date,
  end: Date,
  zoom: ZoomLevel,
): { label: string; start: Date; end: Date }[] {
  const cols: { label: string; start: Date; end: Date }[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const colStart = new Date(cursor);
    let colEnd: Date;
    let label: string;

    if (zoom === "year") {
      label = cursor.getFullYear().toString();
      colEnd = new Date(cursor.getFullYear(), 11, 31);
      cursor.setFullYear(cursor.getFullYear() + 1);
    } else if (zoom === "quarter") {
      const q = Math.floor(cursor.getMonth() / 3) + 1;
      label = `Q${q} ${cursor.getFullYear()}`;
      colEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0);
      cursor.setMonth(cursor.getMonth() + 3);
    } else {
      label = cursor.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      colEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    cols.push({ label, start: colStart, end: colEnd });
  }

  return cols;
}

function getBarPosition(
  projectStart: string | null,
  projectEnd: string | null,
  rangeStart: Date,
  rangeEnd: Date,
): { left: string; width: string } | null {
  if (!projectStart || !projectEnd) return null;

  const totalMs = rangeEnd.getTime() - rangeStart.getTime();
  if (totalMs <= 0) return null;

  const pStart = new Date(projectStart);
  const pEnd = new Date(projectEnd);

  const leftMs = Math.max(0, pStart.getTime() - rangeStart.getTime());
  const rightMs = Math.min(totalMs, pEnd.getTime() - rangeStart.getTime());
  const widthMs = Math.max(0, rightMs - leftMs);

  if (widthMs === 0) return null;

  const leftPct = (leftMs / totalMs) * 100;
  const widthPct = (widthMs / totalMs) * 100;

  return {
    left: `${leftPct}%`,
    width: `${Math.max(widthPct, 1)}%`,
  };
}

function getDatePercent(date: string, rangeStart: Date, rangeEnd: Date): number | null {
  const totalMs = rangeEnd.getTime() - rangeStart.getTime();
  if (totalMs <= 0) return null;
  const ms = new Date(date).getTime() - rangeStart.getTime();
  if (ms < 0 || ms > totalMs) return null;
  return (ms / totalMs) * 100;
}

function formatDate(date: string | null): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type DateType = "contract" | "forecast" | "actual";

const DIAMOND_STYLES: Record<DateType, string> = {
  contract: "bg-white border-gray-400",
  forecast: "bg-amber-400 border-amber-600",
  actual: "bg-emerald-500 border-emerald-700",
};

function MilestoneTooltipBody({ milestone }: { milestone: Milestone }) {
  const rows = [
    { label: "Contract", date: milestone.contractDate, cls: DIAMOND_STYLES.contract },
    { label: "Forecast", date: milestone.forecastDate, cls: DIAMOND_STYLES.forecast },
    { label: "Actual", date: milestone.actualDate, cls: DIAMOND_STYLES.actual },
  ].filter((r) => !!r.date);

  return (
    <div className="space-y-1.5">
      <div className="font-semibold">{milestone.name}</div>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2">
            <span className={`h-2 w-2 rotate-45 border ${r.cls}`} />
            <span className="text-[11px] text-background/70">{r.label}:</span>
            <span className="text-[11px] font-medium">{formatDate(r.date)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Diamond({
  leftPct,
  type,
  milestone,
  onSelect,
}: {
  leftPct: number;
  type: DateType;
  milestone: Milestone;
  onSelect: (m: Milestone) => void;
}) {
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onSelect(milestone)}
          aria-label={`${milestone.name} — ${typeLabel} date`}
          className="group absolute top-1/2 z-10 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#334155] focus-visible:ring-offset-1 focus-visible:rounded"
          style={{ left: `${leftPct}%` }}
        >
          <span
            className={`h-2.5 w-2.5 rotate-45 border shadow-sm transition-transform duration-150 group-hover:scale-150 group-focus-visible:scale-150 ${DIAMOND_STYLES[type]}`}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="px-3 py-2">
        <MilestoneTooltipBody milestone={milestone} />
      </TooltipContent>
    </Tooltip>
  );
}

function MilestoneTrend({
  milestone,
  rangeStart,
  rangeEnd,
  onSelect,
}: {
  milestone: Milestone;
  rangeStart: Date;
  rangeEnd: Date;
  onSelect: (m: Milestone) => void;
}) {
  const points: { pct: number; type: DateType; date: string }[] = [];
  if (milestone.contractDate) {
    const p = getDatePercent(milestone.contractDate, rangeStart, rangeEnd);
    if (p !== null) points.push({ pct: p, type: "contract", date: milestone.contractDate });
  }
  if (milestone.forecastDate) {
    const p = getDatePercent(milestone.forecastDate, rangeStart, rangeEnd);
    if (p !== null) points.push({ pct: p, type: "forecast", date: milestone.forecastDate });
  }
  if (milestone.actualDate) {
    const p = getDatePercent(milestone.actualDate, rangeStart, rangeEnd);
    if (p !== null) points.push({ pct: p, type: "actual", date: milestone.actualDate });
  }

  if (points.length === 0) return null;

  const pcts = points.map((p) => p.pct);
  const minPct = Math.min(...pcts);
  const maxPct = Math.max(...pcts);

  return (
    <>
      {maxPct > minPct && (
        <div
          className="pointer-events-none absolute top-1/2 h-px -translate-y-1/2 bg-gray-400/70"
          style={{ left: `${minPct}%`, width: `${maxPct - minPct}%` }}
        />
      )}
      {points.map((p) => (
        <Diamond
          key={`${milestone.id}-${p.type}`}
          leftPct={p.pct}
          type={p.type}
          milestone={milestone}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-xs text-muted-foreground">
      <span className="font-medium">Milestones:</span>
      {(
        [
          { type: "contract", label: "Contract" },
          { type: "forecast", label: "Forecast" },
          { type: "actual", label: "Actual" },
        ] as const
      ).map(({ type, label }) => (
        <span key={type} className="flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rotate-45 border ${DIAMOND_STYLES[type]}`} />
          {label}
        </span>
      ))}
    </div>
  );
}

export function TimelinePage() {
  const { data: projects, isLoading, isError, error, refetch } = useProjects();
  const [zoom, setZoom] = useState<ZoomLevel>("month");
  const [pmFilter, setPmFilter] = useState<string>(ALL_PMS);
  const [selected, setSelected] = useState<{ milestone: Milestone; projectName: string } | null>(
    null,
  );

  const allItems = projects ?? [];

  const projectManagers = useMemo(() => {
    const set = new Set<string>();
    for (const p of allItems) {
      if (p.projectManager?.trim()) set.add(p.projectManager.trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allItems]);

  const items = useMemo(() => {
    if (pmFilter === ALL_PMS) return allItems;
    return allItems.filter((p) => p.projectManager === pmFilter);
  }, [allItems, pmFilter]);

  const { range, columns } = useMemo(() => {
    if (items.length === 0) return { range: null, columns: [] };
    const r = getTimelineRange(items, zoom);
    return { range: r, columns: generateColumns(r.start, r.end, zoom) };
  }, [items, zoom]);

  const milestoneQueries = useQueries({
    queries: items.map((p) => ({
      queryKey: milestoneKeys.project(p.id),
      queryFn: () => apiFetch<Milestone[]>(`/projects/${p.id}/milestones`),
      enabled: !!p.id,
      staleTime: 60_000,
    })),
  });

  const milestonesByProject = useMemo(() => {
    const acc: Record<string, Milestone[]> = {};
    items.forEach((p, i) => {
      acc[p.id] = milestoneQueries[i]?.data ?? [];
    });
    return acc;
  }, [items, milestoneQueries]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load projects"} onRetry={() => refetch()} />
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">Timeline</h1>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Project Manager</span>
              <Select value={pmFilter} onValueChange={setPmFilter}>
                <SelectTrigger size="sm" className="min-w-[200px]">
                  <SelectValue placeholder="All Project Managers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_PMS}>All Project Managers</SelectItem>
                  {projectManagers.map((pm) => (
                    <SelectItem key={pm} value={pm}>
                      {pm}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1 rounded-lg border p-1">
              {(["month", "quarter", "year"] as const).map((level) => (
                <Button
                  key={level}
                  variant={zoom === level ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setZoom(level)}
                  className="capitalize"
                >
                  {level}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <Legend />

        {items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Calendar className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
              <p>
                {pmFilter === ALL_PMS
                  ? "No projects to display on the timeline."
                  : "No projects for the selected Project Manager."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Column headers */}
                <div className="flex border-b bg-muted/50">
                  <div className="w-56 shrink-0 border-r px-4 py-2 text-sm font-medium text-muted-foreground">
                    Project
                  </div>
                  <div className="flex flex-1">
                    {columns.map((col) => (
                      <div
                        key={col.label}
                        className="flex-1 border-r px-2 py-2 text-center text-xs text-muted-foreground"
                      >
                        {col.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Project rows */}
                {items.map((project) => {
                  const bar = range
                    ? getBarPosition(project.startDate, project.endDate, range.start, range.end)
                    : null;
                  const projectMilestones = milestonesByProject[project.id] ?? [];

                  return (
                    <div
                      key={project.id}
                      className="flex border-b last:border-b-0 hover:bg-muted/30"
                    >
                      <div className="w-56 shrink-0 border-r px-4 py-3">
                        <Link
                          to="/projects/$projectId"
                          params={{ projectId: project.id }}
                          search={{ tab: "overview" }}
                          className="text-sm font-medium hover:underline"
                        >
                          {project.name}
                        </Link>
                        <div className="mt-1 flex items-center gap-2">
                          <Badge variant={statusVariant(project.status)} className="text-[10px]">
                            {project.status}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {formatDate(project.startDate)} - {formatDate(project.endDate)}
                          </span>
                        </div>
                        {project.projectManager && (
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            PM: {project.projectManager}
                          </div>
                        )}
                      </div>
                      <div className="relative flex-1">
                        {/* Grid lines span full row */}
                        <div className="absolute inset-0 flex">
                          {columns.map((col) => (
                            <div key={col.label} className="flex-1 border-r" />
                          ))}
                        </div>
                        {/* Bar lane */}
                        <div className="relative h-10">
                          {bar ? (
                            <div
                              className={`absolute top-1/2 h-6 -translate-y-1/2 rounded-full ${statusColor(project.status)} opacity-80`}
                              style={{ left: bar.left, width: bar.width }}
                              title={`${project.name}: ${formatDate(project.startDate)} - ${formatDate(project.endDate)}`}
                            >
                              <span className="flex h-full items-center justify-center px-2 text-[10px] font-medium text-white truncate">
                                {project.percentComplete ? `${project.percentComplete}%` : ""}
                              </span>
                            </div>
                          ) : (
                            <div className="flex h-full items-center px-4 text-xs text-muted-foreground">
                              No dates set
                            </div>
                          )}
                        </div>
                        {/* Milestones lane */}
                        {range && projectMilestones.length > 0 && (
                          <div className="relative h-6 border-t border-dashed border-muted-foreground/10">
                            {projectMilestones.map((m) => (
                              <MilestoneTrend
                                key={m.id}
                                milestone={m}
                                rangeStart={range.start}
                                rangeEnd={range.end}
                                onSelect={(milestone) =>
                                  setSelected({ milestone, projectName: project.name })
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        <MilestoneDetailDialog
          milestone={selected?.milestone ?? null}
          projectName={selected?.projectName ?? null}
          open={selected !== null}
          onOpenChange={(open) => {
            if (!open) setSelected(null);
          }}
        />
      </div>
    </TooltipProvider>
  );
}
